import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAuthorizedSiteIds } from '@/hooks/queries/useAuthorizedSiteIds';

interface Site {
  id: string;
  name: string;
  location: string;
  status?: string;
  short_code?: string | null;
}

/**
 * 🚀 OPTIMISATION: Converti en React Query pour bénéficier du cache
 * staleTime: 5 minutes - évite les requêtes redondantes
 */
export const useSiteSelector = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  
  // Use centralized site access hook
  const { siteIds, loading: siteIdsLoading } = useAuthorizedSiteIds();

  // Fetch sites using the centralized site IDs
  const { data: sites = [], isLoading: sitesLoading, error: queryError, refetch } = useQuery({
    queryKey: ['sites-selector', user?.id, siteIds],
    queryFn: async () => {
      if (!user?.id || siteIds.length === 0) return [];

      const { data: sitesData, error: sitesError } = await supabase
        .from('sites')
        .select('id, name, location, status, short_code')
        .in('id', siteIds)
        .in('status', ['in_progress', 'planning'])
        .order('name');

      if (sitesError) throw sitesError;

      return sitesData || [];
    },
    enabled: !!user?.id && siteIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Cache 10 minutes
    refetchOnMount: 'always', // Toujours refetch à la navigation
    refetchOnWindowFocus: true,
  });
  
  const loading = siteIdsLoading || sitesLoading;

  // Auto-select site from localStorage or first available
  useEffect(() => {
    if (!user?.id || sites.length === 0) {
      if (!user?.id) {
        setSelectedSite(null);
      }
      return;
    }

    const savedSiteId = localStorage.getItem('selected_site_id');
    
    if (savedSiteId) {
      const savedSite = sites.find(c => c.id === savedSiteId);
      if (savedSite) {
        setSelectedSite(savedSite);
        return;
      }
    }
    
    // Fallback to first site
    setSelectedSite(sites[0]);
    localStorage.setItem('selected_site_id', sites[0].id);
  }, [user?.id, sites]);

  const selectSite = useCallback((site: Site) => {
    if (!user?.id) return;
    setSelectedSite(site);
    localStorage.setItem('selected_site_id', site.id);
    
    // 🚀 CRITICAL: Invalidate all site-specific queries to force fresh data
    queryClient.invalidateQueries({ queryKey: ['chefStats'] });
    queryClient.invalidateQueries({ queryKey: ['site-fresh-data'] });
    queryClient.invalidateQueries({ queryKey: ['site-static-data'] });
    queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
    queryClient.invalidateQueries({ queryKey: ['pending-reservations-count'] });
    
    window.dispatchEvent(new CustomEvent('site:selected', { detail: { id: site.id } }));
  }, [user?.id, queryClient]);

  // Sync selection across components
  useEffect(() => {
    const onSelected = (e: any) => {
      const siteId = e?.detail?.id as string | undefined;
      if (!siteId) return;
      setSelectedSite((prev) => {
        const next = sites.find((c) => c.id === siteId) || prev;
        if (next && next.id !== prev?.id) {
          localStorage.setItem('selected_site_id', next.id);
        }
        return next;
      });
    };
    window.addEventListener('site:selected', onSelected);
    return () => window.removeEventListener('site:selected', onSelected);
  }, [sites]);

  return {
    sites,
    selectedSite,
    selectSite,
    loading,
    error: queryError?.message || null,
    refetch
  };
};
